import dynamic from "next/dynamic";
import type { QuickFilter } from "../../../DataTable-modular/types";
import { getRealmSelectOptions } from "@/lib/integrations/quickbooks/realms";
// UserFilter is loaded LAZILY (next/dynamic), never with a static import, to break a
// module-eval circular dependency: registry → entity Configs → *-filters.ts → this
// file → quick-filter-components.tsx → (@/lib/hooks/use-api / UserSelect) →
// @/lib/entity-registry → back to the Configs. A static import here re-enters this
// module before COMMON_QUICK_FILTERS is initialized and crashes every page that
// loads the registry ("Cannot access 't' before initialization" TDZ). The dynamic
// import defers pulling quick-filter-components.tsx until the filter bar renders
// (client-only), so the cycle edge never exists at module-eval time.
const UserFilter = dynamic(
  () =>
    import("../../../DataTable-modular/filters/quick-filter-components").then(
      (mod) => mod.UserFilter
    ),
  { ssr: false }
);

// ============================================================================
// COMMON QUICK FILTERS
// ============================================================================

// Common quick filter configurations that can be reused across tables
export const COMMON_QUICK_FILTERS = {
  // Client filter - can be used in time entries, tickets, etc.
  client: {
    id: "client",
    label: "Client",
    type: "client" as const,
    field: "contact_id", // Field should be the ID
    placeholder: "Select a client...",
    // New filter function to correctly filter by client ID
    filterFn: (row: any, filterValue: any) => {
      // Direct contact_id (tickets, communications)
      if (row.contact_id === filterValue) return true;
      // Nested contacts (legacy)
      if (row.contacts && typeof row.contacts === "object" && "id" in row.contacts) {
        return row.contacts.id === filterValue;
      }
      // Time entries: contact via ticket
      if (row.tickets?.contact_id === filterValue) return true;
      return false;
    },
  },

  // Approval status filter
  approvalStatus: {
    id: "approval_status",
    label: "Approval Status",
    type: "select" as const,
    field: "is_approved",
    placeholder: "Select status...",
    options: [
      { label: "Approved", value: true },
      { label: "Not Approved", value: false },
    ],
  },

  // Billable status filter (maps to entry_type)
  billableStatus: {
    id: "billable_status",
    label: "Billable Status",
    type: "select" as const,
    field: "entry_type",
    placeholder: "Select status...",
    options: [
      { label: "Billable", value: "billable" },
      { label: "Non-Billable", value: "non_billable" },
    ],
  },

  // Date range filter
  dateWorked: {
    id: "date_worked",
    label: "Date Worked",
    type: "date-range" as const,
    field: "date_worked",
    placeholder: "Select date range...",
  },

  // Hours filter
  hours: {
    id: "hours",
    label: "Hours",
    type: "number" as const,
    field: "hours",
    placeholder: "Enter hours...",
  },

  // Description search
  description: {
    id: "description",
    label: "Description",
    type: "text" as const,
    field: "description",
    placeholder: "Search description...",
  },

  // User filter (use unified UserSelect component)
  user: {
    id: "user",
    label: "User",
    type: "text" as const,
    field: "assigned_to",
    placeholder: "Search by user...",
    component: UserFilter,
  },

  // Ticket number filter
  ticketNumber: {
    id: "ticket_number",
    label: "Ticket Number",
    type: "text" as const,
    field: "ticket_number",
    placeholder: "Enter ticket number...",
  },

  // QuickBooks billing company (realm) filter. Default field targets
  // recurring_transactions (`quickbooks_realm_id`); recurring_charges spreads
  // this and overrides `field` to `realm_id`. Options derive from the realm
  // registry so a new realm appears automatically.
  realm: {
    id: "realm",
    label: "Billing Company",
    type: "select" as const,
    field: "quickbooks_realm_id",
    placeholder: "Select billing company...",
    options: getRealmSelectOptions(),
  },

  // Sync status filter
  syncStatus: {
    id: "sync_status",
    label: "Sync Status",
    type: "select" as const,
    field: "sync_status",
    placeholder: "Select sync status...",
    options: [
      { label: "Not Synced", value: "not_synced" },
      { label: "Pending", value: "pending" },
      { label: "Synced", value: "synced" },
      { label: "Failed", value: "failed" },
    ],
  },
};
